import { useState, useCallback, useEffect } from 'react';
import type { FeedItem, FeedResponse } from '../types';
import * as socialApi from '../services/social-api';

export interface UseFeedReturn {
  items: FeedItem[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook for managing the social feed with cursor-based pagination.
 */
export function useFeed(): UseFeedReturn {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(true);

  const loadFeed = useCallback(async (cursor?: string, append = false) => {
    try {
      setIsLoading(true);
      setError(null);
      const data: any = await socialApi.fetchFeed(cursor);
      const feedItems = data.items || data.data || [];
      setItems((prev) => (append ? [...prev, ...feedItems] : feedItems));
      setNextCursor(data.nextCursor);
      setHasMore(data.hasMore ?? false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feed');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;
    await loadFeed(nextCursor, true);
  }, [hasMore, isLoading, nextCursor, loadFeed]);

  const refresh = useCallback(async () => {
    setNextCursor(undefined);
    await loadFeed();
  }, [loadFeed]);

  return { items, isLoading, error, hasMore, loadMore, refresh };
}
